<template>
  <v-card elevation="0" outlined :loading="isLoading">
    <v-expansion-panels flat :value="0" class="py-1">
      <v-expansion-panel>
        <v-expansion-panel-header hide-actions>
          <template v-slot:default="{ open }">
            <span
              class="subtitle-2 font-weight-bold blue-grey--text text--darken-3"
            >
              <v-icon
                class="mr-3 mt-n1"
                size="25"
                color="blue-grey darken-3"
                @click="handleGroupExpansion(open)"
              >
                <template v-if="open">mdi-chevron-up</template>
                <template v-else>mdi-chevron-down</template>
              </v-icon>
              {{ title }}
            </span>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <EmptyState
            v-if="!isLoading && !hasTasks"
            class="py-6 px-6"
            :icon="'mdi-emoticon-happy-outline'"
            :iconSize="36"
            :title="$t('tasks.empty_state_title')"
            :message="$t('tasks.empty_state_message')"
          />
          <v-data-table
            v-if="hasTasks"
            class="ma-0 pa-0 mt-n6"
            :key="rows.length"
            hide-default-footer
            disable-pagination
            disable-filtering
            disable-sort
            :headers="headers"
            :items="rows"
          >
            <template v-slot:item="{ item, index }">
              <v-hover v-slot="{ hover }">
                <tr
                  @click="$emit('selectTask', item.id)"
                  class="cursor-pointer task-item-row"
                  :class="{
                    'blue lighten-2': activeTask === item.id,
                  }"
                >
                  <td>
                    <div class="d-flex justify-start align-center">
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-icon
                            v-on="on"
                            class="mr-2 cursor-pointer"
                            data-tourId="tasks-table-markAsCompleted"
                            :color="item.isCompleted ? 'green' : ''"
                            @click.native.stop="
                              $emit('radioClick', item.status, item.id, index)
                            "
                          >
                            {{
                              item.isCompleted
                                ? 'mdi-check-circle'
                                : 'mdi-radiobox-blank'
                            }}
                          </v-icon>
                        </template>
                        {{
                          item.isCompleted
                            ? $t('tasks.mark_as_open')
                            : $t('tasks.mark_as_completed')
                        }}
                      </v-tooltip>

                      <span
                        class="subtitle ma-0 pa-0 font-weight-medium"
                        v-snip="2"
                        :class="{
                          completed: item.isCompleted,
                        }"
                        >{{ item.name }}</span
                      >
                    </div>
                  </td>

                  <td data-tourId="tasks-table-dueDate">
                    <div
                      class="d-flex align-center"
                      :class="{
                        completed: item.isCompleted,
                      }"
                    >
                      <span
                        class="caption mr-1"
                        :class="
                          item.isOverDue
                            ? 'red--text'
                            : 'blue-grey--text text--darken-1'
                        "
                        >{{ item.dueDateText }}
                      </span>
                      <v-icon v-if="item.isOverDue" color="red"
                        >mdi-alert-circle-outline
                      </v-icon>
                    </div>
                  </td>
                  <td data-tourId="tasks-table-assignee">
                    <div
                      :class="{
                        completed: item.isCompleted,
                      }"
                    >
                      <ChipAvatar
                        class="caption"
                        hideBorder
                        small
                        :account="item.assignees[0]"
                      />
                    </div>
                  </td>

                  <td data-tourId="tasks-table-priority">
                    <div
                      :class="{
                        completed: item.isCompleted,
                      }"
                    >
                      <TaskPriority
                        v-if="item.priority"
                        :priority="item.priority"
                      />
                    </div>
                  </td>

                  <td data-tourId="tasks-table-status">
                    <StatusChip v-if="item.status" :status="item.status" />
                  </td>

                  <td data-tourId="tasks-table-tags">
                    <ChipList
                      outlined
                      class="task-tags-chip mt-1"
                      :items="getTaskTagNames(item)"
                    />
                  </td>

                  <td data-tourId="task-table-privacy">
                    <div
                      class="ma-0 cursor-pointer pa-0 d-flex justify-center align-center"
                    >
                      <v-icon color="blue-grey" size="16"
                        >{{ item.isPrivate ? 'mdi-lock' : 'mdi-earth' }}
                      </v-icon>

                      <span
                        class="caption ml-2 blue-grey--text text--darken-1"
                        >{{
                          item.isPrivate
                            ? $t('tasks.private')
                            : $t('tasks.public')
                        }}</span
                      >
                    </div>
                  </td>

                  <td data-tourId="tasks-table-actions">
                    <div
                      class="d-flex"
                      :class="{
                        completed: item.isCompleted,
                      }"
                    >
                      <v-tooltip v-if="hover" top>
                        <template v-slot:activator="{ on }">
                          <div
                            class="mr-1 cursor-pointer bubble pa-2 d-flex justify-center"
                          >
                            <v-icon
                              v-on="on"
                              size="18"
                              color="blue-grey darken-1"
                              @click.native.stop="copyToClipboard(item.id)"
                              >mdi-link-variant
                            </v-icon>
                          </div>
                        </template>
                        {{ $t('common.copy_link') }}
                      </v-tooltip>
                      <v-tooltip v-if="hover && item.canEdit" top>
                        <template v-slot:activator="{ on }">
                          <div
                            class="mr-1 cursor-pointer bubble pa-2 d-flex justify-center"
                          >
                            <v-icon
                              v-on="on"
                              size="18"
                              color="blue-grey darken-1"
                              @click.native.stop="$emit('duplicate', item)"
                              >mdi-content-copy
                            </v-icon>
                          </div>
                        </template>
                        {{ $t('common.button.duplicate') }}
                      </v-tooltip>
                      <v-tooltip v-if="hover && item.canDelete" top>
                        <template v-slot:activator="{ on }">
                          <div
                            class="mr-1 cursor-pointer bubble pa-2 d-flex justify-center"
                          >
                            <v-icon
                              v-on="on"
                              color="blue-grey darken-1"
                              size="18"
                              @click.native.stop="$emit('delete', item.id)"
                              >mdi-delete-outline
                            </v-icon>
                          </div>
                        </template>
                        {{ $t('common.button.delete') }}
                      </v-tooltip>
                    </div>
                  </td>
                </tr>
              </v-hover>
            </template>
          </v-data-table>
          <v-divider v-if="shouldPaginate" />
          <div class="text-center pt-1 mb-n3" v-if="shouldPaginate">
            <v-btn
              text
              plain
              color="blue"
              class="v-btn--active"
              :ripple="false"
              :loading="isLoading"
              @click="$emit('viewMore')"
            >
              {{ $t('common.button.view_more') }}
            </v-btn>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>
<script>
import moment from 'moment'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import taskPermissions from '@/mixins/taskPermissions.vue'
import mixin from '@/mixins/mixin.vue'
import config from '@/config'

import ChipAvatar from '@/components/ChipAvatar.vue'
import TaskPriority from '@/components/Priority.vue'
import StatusChip from '@/components/Core/StatusChip.vue'
import EmptyState from '@/components/EmptyState.vue'
import ChipList from '@/components/ChipList.vue'

export default {
  name: 'ExpansionTable',

  mixins: [mixin, trackerMixin, taskPermissions],

  components: {
    ChipAvatar,
    TaskPriority,
    EmptyState,
    StatusChip,
    ChipList,
  },

  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    shouldPaginate: {
      type: Boolean,
      default: false,
    },
    groupKey: {
      type: String,
      default: () => '',
    },
  },

  data() {
    return {
      selected: '',
    }
  },

  computed: {
    headers() {
      return [
        {
          text: '',
          value: 'name',
          show: false,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('filters.due_date'),
          value: 'dueDate',
          show: true,
          cellClass: 'cursor-pointer',
          width: '150px',
        },
        {
          text: this.$t('common.assignee_one'),
          value: 'assignees',
          show: true,
          cellClass: 'cursor-pointer',
          width: '180px',
        },
        {
          text: this.$t('common.priority'),
          value: 'priority',
          show: true,
          cellClass: 'cursor-pointer',
          align: 'start',
          width: '132px',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          show: true,
          cellClass: 'cursor-pointer',
          align: 'start',
          width: '132px',
        },
        {
          text: this.$t('common.tag_other'),
          value: 'tags',
          show: true,
          cellClass: 'cursor-pointer',
          align: 'start',
        },
        {
          text: this.$t('tasks.privacy'),
          value: 'privacy',
          show: false,
          width: '40px',
        },
        {
          text: '',
          value: 'menu',
          show: false,
          align: 'start',
          width: '145px',
        },
      ]
    },

    rows() {
      return !_.isEmpty(this.tasks)
        ? this.tasks.map(task => {
            return {
              ...task,
              tags:
                task?.tags?.map((tag, index) => {
                  if (tag?.name?.length >= 25 && index === 0) {
                    return { ...tag, name: `${tag?.name?.slice(0, 25)}...` }
                  }
                  return tag
                }) ?? [],

              dueDateText: task?.dueDate
                ? utils.dates.humanDueDate(task.dueDate)
                : '',
              isOverDue: task?.dueDate
                ? moment(task.dueDate)
                    .endOf('day')
                    .isBefore(moment().endOf('day')) &&
                  task.status !== this.$constants.tasksStatus.completed
                : false,
              isCompleted:
                task.status === this.$constants.tasksStatus.completed,
              assignees: task.assignees || [],
              canEdit: this.canEditTask({ task }),
              canDelete: this.canDeleteTask({ task }),
            }
          })
        : []
    },

    activeTask() {
      return this.$route.query?.task
    },

    emptyStateMessage() {
      return this.$t('routines.profile_findings_empty')
    },

    hasTasks() {
      return !_.isEmpty(this.tasks)
    },
  },

  methods: {
    getTaskTagNames(item) {
      return item?.tags?.map(tag => tag.name) ?? []
    },

    copyToClipboard(taskId) {
      navigator.clipboard.writeText(`${config.webAppURL}/tasks?task=${taskId}`)
      this.showInfo(this.$t('common.link_copy_to_clipboard'))
      this.trackTasks('tasks.copyLink', {
        taskId: taskId,
        from: 'taskView',
      })
    },

    handleGroupExpansion(value) {
      this.trackTasks('tasks.expandGroup', {
        group: this.groupKey,
        value: !value,
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.completed {
  opacity: 0.5;
}

.bubble {
  &:hover {
    box-shadow: inset 18px 18px 18px 18px $color-blue-grey-lighten2 !important;
    border-radius: 50%;
  }
  width: 30px;
  height: 30px;
}
</style>
<style lang="scss">
.task-tags-chip {
  .v-chip.v-size--small {
    height: 20px !important;
    border-color: $color-blue-grey-lighten1 !important;
    span {
      font-size: $font-size-12 !important;
      line-height: 8px !important;
      color: $color-blue-grey-darken2 !important;
    }
  }
}
</style>
